import { FormEvent, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Container from '@mui/material/Container'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'

import { AuthenticationApi } from '../../clients'
import { getConfiguration } from '../../clients/configuration'
import { AppAlert } from '../../components/AppAlerts/AppAlerts'
import { addAlert } from '../../redux/features/alerts/alertsSlice'
import { useAppDispatch } from '../../redux/hooks'
import { RecaptchaService } from '../../services/recaptchaService'
import { getErrorModel } from '../../utils/getErrorModel'

export const CreateAccount = () => {

    const dispatch = useAppDispatch()
    const [email, setEmail] = useState<string>('')
    const [emailError, setEmailError] = useState<string>('')
    const [submitting, setSubmitting] = useState<boolean>(false)

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (emailError) {
            return
        }
        const recaptchaService = new RecaptchaService()
        const token = await recaptchaService.getToken()
        setSubmitting(true)
        const config = await getConfiguration()
        try {
            await new AuthenticationApi(config).createAccount({
                createAccountRequest: {
                    username: email,
                    recaptchaToken: token,
                }
            })
            const alert = new AppAlert('success', 'Account created successfully. Please check your email to verify your account.', true)
            dispatch(addAlert(alert.toJSON()))
        } catch (err) {
            const e = await getErrorModel(err)
            if (e?.errors?.length > 0) {
                const message = e.errors[0].message
                if (message === 'account already exists') {
                    const alert = new AppAlert('error', 'An account with this email already exists', true)
                    dispatch(addAlert(alert.toJSON()))
                    return
                }
            }
            const alert = new AppAlert(
                'error',
                'Something went wrong. Please try again later.',
                true
            )
            dispatch(addAlert(alert.toJSON()))
        } finally {
            setSubmitting(false)
        }
    }

    const handleEmailFocus = () => {
        setEmailError('')
    }

    const handleEmailBlur = (): string => {
        if (!email) {
            return ''
        }
        const emailRegex = new RegExp(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/g)
        if (!email.match(emailRegex)?.length) {
            const error = 'Invalid email address'
            setEmailError(error)
            return error
        }
        return ''
    }

    return (
        <Box sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', py: 4 }}>
            <Container component='div' maxWidth='sm'>
                <Paper elevation={3} sx={{ borderRadius: 2 }}>
                    <Card>
                        <CardContent sx={{ p: 4 }}>
                            <Stack spacing={3} alignItems='center'>
                                <Box
                                    sx={{
                                        width: 64,
                                        height: 64,
                                        borderRadius: 2,
                                        bgcolor: 'primary.main',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <PersonAddOutlinedIcon sx={{ color: 'white', fontSize: 32 }} />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography component="h1" variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                        Create Account
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary'>
                                        Enter your email to get started
                                    </Typography>
                                </Box>
                                <Box component='form' onSubmit={handleSubmit} sx={{ width: '100%' }}>
                                    <Stack spacing={2.5}>
                                        <TextField
                                            required
                                            fullWidth
                                            value={email}
                                            error={!!emailError}
                                            helperText={emailError}
                                            onFocus={handleEmailFocus}
                                            onBlur={handleEmailBlur}
                                            onChange={(e) => setEmail(e.target.value)}
                                            label='Email'
                                            autoComplete='email'
                                            autoCapitalize='off'
                                            autoCorrect='off'
                                            autoFocus
                                        />
                                        <Button
                                            disabled={submitting || !!emailError || !email}
                                            type='submit'
                                            fullWidth
                                            variant="contained"
                                            size="large"
                                        >
                                            {submitting ? 'Creating Account...' : 'Sign Up'}
                                        </Button>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant='caption' color='text.secondary'>
                                                This site is protected by reCAPTCHA and the Google&nbsp;
                                                <Link href="https://policies.google.com/privacy" target="_blank" rel="noopener">Privacy Policy</Link> and&nbsp;
                                                <Link href="https://policies.google.com/terms" target="_blank" rel="noopener">Terms of Service</Link> apply.
                                            </Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', mt: 2 }}>
                                            <Typography variant='body2' color='text.secondary'>
                                                Already have an account?{' '}
                                                <RouterLink to='/auth/sign-in' style={{ textDecoration: 'none' }}>
                                                    <Link component='span' sx={{ fontWeight: 600 }}>Sign in</Link>
                                                </RouterLink>
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Paper>
            </Container>
        </Box>
    )
}